import { cacheExchange, Client, createClient, fetchExchange } from '@urql/core';

import { logError } from '@/utils/miscUtils';

export interface ConsentManagementPayload {
	purposes: Array<{
		Id: string;
		TransactionType: 'CONFIRMED' | 'NOTGIVEN';
		ConsentType: 'IMPLICIT' | 'EXPLICIT';
	}>;
	brand: string;
	source: string;
	externalId: string | null;
	dsDataElements: {
		Email: string;
		Mobile: string;
		FirstName: string;
		LastName: string;
		Zip: string;
	};
}

export interface ConsentManagementVariables {
	brand: 'CLEAR_CHOICE';
	payload: ConsentManagementPayload;
}

export interface ConsentManagementResponse {
	consentManagement: {
		message: string;
		success: boolean;
	};
}

const CONSENT_MANAGEMENT_MUTATION = `
  mutation ConsentManagement($brand: BffBrandType!, $payload: JSON!) {
    consentManagement(brand: $brand, payload: $payload) {
      message
      success
    }
  }
`;

let beffeGraphQlClient: Client | null = null;

/**
 * Creates or returns a cached BEFFE GraphQL client.
 * Uses the same pattern as schedule-consultation's getBeffeGraphQlClient.
 */
export const getBeffeGraphQlClient = (url: string) => {
	if (beffeGraphQlClient === null) {
		beffeGraphQlClient = createClient({
			url,
			exchanges: [cacheExchange, fetchExchange],
			requestPolicy: 'network-only',
			fetchOptions: () => {
				return {
					headers: {
						'content-type': 'application/json',
					},
				};
			},
		});
	}
	return beffeGraphQlClient;
};

/**
 * Submit consent management data to the BEFFE GraphQL endpoint.
 * Called after successful form submission when privacy_consent is true.
 *
 * using urql client instead of raw fetch.
 */
export const submitConsentData = async (
	optIn: boolean,
	bffUrl: string,
	salesforceId: string | undefined,
	userDetails: {
		Email: string;
		Mobile: string;
		FirstName: string;
		LastName: string;
		Zip: string;
	}
): Promise<ConsentManagementResponse | null> => {
	if (!salesforceId || !bffUrl) {
		return null;
	}

	try {
		const beffeClient = getBeffeGraphQlClient(bffUrl);

		const variables: ConsentManagementVariables = {
			brand: 'CLEAR_CHOICE',
			payload: {
				purposes: [
					{
						Id: 'Transactional_Email',
						TransactionType: 'CONFIRMED',
						ConsentType: 'IMPLICIT',
					},
					{
						Id: 'Transactional_SMS',
						TransactionType: 'CONFIRMED',
						ConsentType: 'IMPLICIT',
					},
					{
						Id: 'Marketing_Email',
						TransactionType: 'CONFIRMED',
						ConsentType: 'IMPLICIT',
					},
					{
						Id: 'Marketing_SMS',
						TransactionType: optIn ? 'CONFIRMED' : 'NOTGIVEN',
						ConsentType: optIn ? 'EXPLICIT' : 'IMPLICIT',
					},
					{
						Id: 'Outbound_Calling',
						TransactionType: optIn ? 'CONFIRMED' : 'NOTGIVEN',
						ConsentType: optIn ? 'EXPLICIT' : 'IMPLICIT',
					},
				],
				brand: 'CLEAR_CHOICE',
				source: 'convertflow-digital-marketing',
				externalId: salesforceId,
				dsDataElements: {
					Email: userDetails.Email,
					Mobile: userDetails.Mobile,
					FirstName: userDetails.FirstName,
					LastName: userDetails.LastName,
					Zip: userDetails.Zip,
				},
			},
		};

		const result = await beffeClient.mutation(CONSENT_MANAGEMENT_MUTATION, variables).toPromise();

		if (result.error) {
			logError(`Consent submission error: ${result.error.message}`);
			return null;
		}

		return result.data as ConsentManagementResponse;
	} catch (error) {
		logError(`Failed to submit consent data: ${error instanceof Error ? error.message : 'unknown error'}`);
		return null;
	}
};
